import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Color } from '@/constants/color';
import {
  OnboardingContextActionType,
  useOnboardingContext,
  useOnboardingDispatch
} from '@/contexts/onboardingContext';
import useMixpanel from '@/hooks/useTracking/useMixpanel';
import useTranslate from '@/hooks/useTranslate';
import MobileAppLink from '@/pages/members/components/MobileAppLink';
import { useGetOneTimeToken } from '@/pages/members/hooks/useGetOneTimeToken';
import GradientBg from '@/pages/members/pages/Onboarding/components/GradientBg';
import { PAGES_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import AuthRepository from '@/repositories/members/AuthRepository';
import Button from '@/ui/members/components/Button';
import Spinner from '@/ui/members/components/Spinner';
import Heading from '@/ui/members/components/typography/Heading';

const RESEND_TIMEOUT = 30; // seconds

const OnboardingCompleted = () => {
  const {
    completed,
    data: { emailAddress },
    loginLink
  } = useOnboardingContext();
  const authRepository = AuthRepository();
  const dispatch = useOnboardingDispatch();
  const onboardingContext = useOnboardingContext();
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(RESEND_TIMEOUT);
  const [resendEnabled, setResendEnabled] = useState(false);
  const {
    refetch: refetchOneTimeToken,
    isPending: oneTimeTokenPending,
    isRefetching: oneTimeTokenRefetching
  } = useGetOneTimeToken({});
  const translateKey = `${PAGES_PATH_TRANSLATION_KEY}.OnboardingCompleted`;
  const { t } = useTranslate(translateKey);
  const mixpanel = useMixpanel();

  useEffect(() => {
    mixpanel.track('Screen Visited', {
      screenName: `onboarding:completed`,
      version: onboardingContext.version,
      feature: 'unified-onboarding'
    });
  });

  const handleLogout = () => {
    authRepository.logout().then(() => {
      dispatch({
        type: OnboardingContextActionType.RESET
      });
      navigate('/');
    });
  };

  const handleResend = () => {
    if (resendEnabled) {
      setResendEnabled(false);
      authRepository.sendOneTimeToken();
      refetchOneTimeToken().then((response) => {
        dispatch({
          type: OnboardingContextActionType.SET_LOGIN_LINK,
          payload:
            response.data?.link || `${process.env.VITE_APP_WEBSITE_URL}/download-and-schedule`
        });
        setSecondsLeft(RESEND_TIMEOUT);
      });
    }
  };

  useEffect(() => {
    setResendEnabled(false);
    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setResendEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft]);

  useEffect(() => {
    // if we are in a mobile browser, redirect them to the deeplink which should:
    // 1. open the app and log them in if installed
    refetchOneTimeToken()
      .then((response) => {
        dispatch({
          type: OnboardingContextActionType.SET_LOGIN_LINK,
          payload:
            response.data?.link || `${process.env.VITE_APP_WEBSITE_URL}/download-and-schedule`
        });
        return;
      })
      .catch(() => {
        dispatch({
          type: OnboardingContextActionType.SET_LOGIN_LINK,
          payload: `${process.env.VITE_APP_WEBSITE_URL}/download-and-schedule`
        });
      });
  }, [completed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loginLink && isMobile) {
      window.open(loginLink);
      return;
    }
  }, [loginLink]);

  return (
    <>
      <Heading className="text-center mb-8" level={1}>
        {t('downloadTheApp')}
      </Heading>

      <GradientBg imgClassName="w-[125%] max-w-[125%] z-0 left-[-12%] right-[13%] top-[-50%] rotate-180">
        <div className="flex flex-col items-center gap-6 mb-8">
          <MobileAppLink store="google" />
          <MobileAppLink store="apple" />
        </div>
      </GradientBg>

      <div className="relative flex flex-col items-center justify-center">
        {oneTimeTokenPending || oneTimeTokenRefetching ? (
          <Spinner className="mb-8 p-12" visibleLabel={false} size={20} />
        ) : (
          <QRCodeSVG
            className="rounded-lg border-8 border-uniform-white-base hidden sm:block mb-8 text-center m-auto"
            value={loginLink}
            size={180}
          />
        )}

        <p className="mb-6 text-center">
          <Trans
            i18nKey={`${translateKey}.oneTimeUseLinkSent`}
            values={{ email: emailAddress }}
            components={{ bold: <b /> }}
          />
        </p>

        <div className="text-center">
          <Button
            className="mb-6"
            color={Color.GREEN}
            onClick={handleResend}
            disabled={!resendEnabled}>
            {resendEnabled ? t('getNewCode') : t('getNewCodeIn', { secondsLeft })}
          </Button>

          <div>
            <Button color={Color.WHITE} onClick={handleLogout}>
              {t('logout')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingCompleted;
